import { Checkbox, Table } from "antd";
import React, { forwardRef, memo, useEffect, useState } from "react";
import styled from "styled-components";

const MediTable = forwardRef(function MediTable(
  {
    dataSource = [],
    totalCount,
    onChangePage,
    tableColumns,
    onRow,
    onRenderFooter,
    enableSelectAll,
    rowKeyField = "id",

    // Callback
    onRowSelectChange,

    // Options
    bordered = false,
    components,
    getCheckboxProps,
    rowSelectionType = "checkbox",
    rowKeyValue = [],
    pageSize = 10,
    limitY = null,
    summary,
    enablePagination = true,
    className,

    ...rest
  },
  ref,
) {
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [expandedRowKeys, setExpandedRowKeys] = useState([]);

  useEffect(() => {
    if (!rowKeyValue) return;

    setSelectedRowKeys((prev) => {
      if (
        prev.length === rowKeyValue.length &&
        prev.every((v, i) => v === rowKeyValue[i])
      ) {
        return prev; // 🚫 không set → không render
      }
      return rowKeyValue;
    });
  }, [rowKeyValue]);

  const handleSelect = (record, selected) => {
    let newKeys = [];

    if (selected) {
      newKeys = [...rowKeyValue, record[rowKeyField]];
    } else {
      newKeys = rowKeyValue.filter((key) => key !== record[rowKeyField]);
    }

    setSelectedRowKeys(newKeys);
    onRowSelectChange?.(newKeys);
  };

  const toggleSelectAll = ({ target }) => {
    const sourceIds = (dataSource || [])
      .filter((x) => {
        const props = getCheckboxProps?.(x) || {};
        return x[rowKeyField] && !props.disabled;
      })
      .map((x) => x[rowKeyField]);

    const oldIds = rowKeyValue.filter((x) => !sourceIds.includes(x));

    let newKeys = [];

    if (target.checked) {
      newKeys = [...oldIds, ...sourceIds];
    } else {
      newKeys = oldIds;
    }

    setSelectedRowKeys(newKeys);
    onRowSelectChange?.(newKeys);
  };

  const headerCheckbox = (
    <Checkbox
      checked={
        dataSource?.length > 0 && selectedRowKeys?.length === dataSource?.length
      }
      indeterminate={
        selectedRowKeys?.length > 0 &&
        selectedRowKeys?.length < dataSource?.length
      }
      onChange={toggleSelectAll}
    />
  );

  const rowSelection =
    enableSelectAll && rowSelectionType
      ? {
          selectedRowKeys,
          type: rowSelectionType,
          fixed: true,
          onSelect: rowSelectionType === "checkbox" ? handleSelect : undefined,
          columnTitle:
            rowSelectionType === "checkbox" ? headerCheckbox : undefined,
          onChange: (keys) => {
            if (rowSelectionType === "radio") {
              setSelectedRowKeys(keys);
              onRowSelectChange?.(keys);
            }
          },
          getCheckboxProps,
        }
      : null;

  const handleExpand = (expanded, record) => {
    const rowKey = record[rowKeyField];
    setExpandedRowKeys((prev) =>
      expanded ? [...prev, rowKey] : prev.filter((key) => key !== rowKey),
    );
  };

  const expandableConfig = rest?.expandable || {};

  const handleTableChange = (pagination, filters, sorter) => {
    if (onChangePage) {
      onChangePage(pagination.current, pagination.pageSize);
    }
  };

  return (
    <CustomTable
      ref={ref}
      expandable={{
        ...expandableConfig,
        expandedRowKeys,
        onExpand: handleExpand,
      }}
      components={components}
      rowSelection={rowSelection}
      columns={tableColumns}
      dataSource={dataSource}
      scroll={{
        x: 1500,
        y: limitY,
      }}
      tableLayout="fixed"
      rowKey={(item) => item[rowKeyField]}
      footer={onRenderFooter}
      bordered={bordered}
      onRow={onRow}
      onChange={handleTableChange}
      pagination={
        enablePagination
          ? {
              showSizeChanger: true,
              total: totalCount ?? dataSource?.length,
              showTotal: (total, range) =>
                `Hiển thị: ${range[0]}-${range[1]} / ${totalCount ?? total} bản ghi`,
              defaultPageSize: pageSize,
              position: ["bottomRight"],
              pageSizeOptions: ["10", "20", "50", "100"],
            }
          : false
      }
      summary={summary}
      className={className}
      {...rest}
    />
  );
});

const CustomTable = styled(Table)`
  .ant-form-item {
    margin-bottom: 0px;
  }
`;

export default memo(MediTable);
